import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Users } from 'lucide-react';

interface Profile {
  id: string;
  email: string;
  avatar_url?: string;
  display_name?: string;
  created_at: string;
}

export default function RecentUsersStats() {
  const [recentUsers, setRecentUsers] = useState<Profile[]>([]);
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchUserStats() {
      try {
        setLoading(true);
        
        // The cutoff date - May 29th, 2025
        const cutoffDate = '2025-05-29T00:00:00Z';
        
        // Get count of users who joined before the cutoff date
        const { count: beforeCutoffCount, error: beforeError } = await supabase
          .from('profiles')
          .select('*', { count: 'exact', head: true })
          .lt('created_at', cutoffDate);
          
        if (beforeError) throw beforeError;
        
        // Get count of paid subscribers who joined on or after the cutoff date
        const { count: paidAfterCutoffCount, error: paidError } = await supabase
          .from('profiles')
          .select('*', { count: 'exact', head: true })
          .gte('created_at', cutoffDate)
          .eq('is_subscribed', true);
          
        if (paidError) throw paidError;
        
        // Calculate the total display count
        // Users before cutoff date + paid subscribers after cutoff date
        let displayCount = 0;
        
        if (beforeCutoffCount !== null && paidAfterCutoffCount !== null) {
          displayCount = beforeCutoffCount + paidAfterCutoffCount;
          setTotalUsers(displayCount);
        }
        
        // Fetch recent users (limited to 8)
        const { data: recentData, error: recentError } = await supabase
          .from('profiles')
          .select('id, email, avatar_url, display_name, created_at')
          .order('created_at', { ascending: false })
          .limit(8);
          
        if (recentError) throw recentError;
        
        if (recentData) {
          setRecentUsers(recentData);
        }
      } catch (error) {
        console.error('Error fetching user stats:', error);
      } finally {
        setLoading(false);
      }
    }
    
    fetchUserStats();
  }, []);
  
  // Function to truncate and mask email
  const formatEmail = (email: string) => {
    if (!email) return '';
    const [username] = email.split('@');
    if (username.length <= 3) {
      return `${username}...`;
    }
    return `${username.substring(0, 3)}...`;
  };
  
  // Get color based on index
  const getAvatarColor = (index: number) => {
    const colors = [
      'from-pink-500 to-rose-500',
      'from-blue-500 to-indigo-500',
      'from-green-500 to-emerald-500',
      'from-amber-500 to-orange-500',
      'from-purple-500 to-violet-500',
      'from-cyan-500 to-sky-500',
      'from-red-500 to-pink-500',
      'from-teal-500 to-cyan-500',
    ];
    return colors[index % colors.length];
  };

  return (
    <div className="mt-4">
      <div className="relative">
        {loading ? (
          <div className="flex items-center justify-center h-12 text-gray-400">
            <div className="w-5 h-5 border-2 border-t-transparent border-[#c9fffc] rounded-full animate-spin mr-2"></div>
            Loading user stats...
          </div>
        ) : (
          <div>
            <div className="flex items-center gap-1 mb-1">
              <div className="flex -space-x-3">
                {recentUsers.map((user, index) => (
                  <div 
                    key={user.id} 
                    className="relative group"
                  >
                    <div 
                      className={`w-8 h-8 rounded-full ring-2 ring-gray-900 bg-gradient-to-br ${getAvatarColor(index)} flex items-center justify-center text-white text-xs font-medium shadow-lg transition-transform duration-300 hover:scale-110 hover:z-10 overflow-hidden`}
                    >
                      {user.avatar_url ? (
                        <img 
                          src={user.avatar_url} 
                          alt={user.display_name || formatEmail(user.email)} 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        user.display_name?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || '?'
                      )}
                    </div>
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-300 whitespace-nowrap">
                      {formatEmail(user.email)}
                    </div>
                  </div>
                ))}
              </div>
              <div className="pl-2 flex items-center">
                <Users className="w-4 h-4 text-[#c9fffc] mr-1" />
                <span className="text-white text-sm font-medium">{totalUsers.toLocaleString()}</span>
                <span className="text-gray-400 text-xs ml-1">users joined</span>
              </div>
            </div>
            <div className="text-xs text-gray-400">
              Join our growing community today!
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
